import { action, computed, makeObservable, observable, reaction, runInAction } from 'mobx';
import { find, get, isEmpty, isNil, lowerCase, map, pick, remove } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import * as Sentry from '@sentry/browser';
import { message } from 'antd';
import i18n from '@/i18n/i18n';

import epaxios from '@/utils/epaxios';
import dialCodes from '@/constants/dialCodes';
import RootStore from './RootStore';
import { LanguageEnum } from '@/constants/enums';
import { getFullStationName } from '@/utils/util-functions';
import { compact } from 'lodash/fp';

const ssoUserEditableFields = [
  'measurementSystem',
  'notificationSubscriptions',
  'notificationsEnabled',
  'language',
  'userStations',
  'userFavoriteStations',
];
interface GetOptions {
  loadMore?: boolean;
  search?: string;
  resetSearch?: boolean;
}

export default class UsersStore {
  rootStore: typeof RootStore;
  @observable users = [];
  @observable isLoading = false;
  @observable isUpdating = false;
  @observable isLoadingMore = false;
  @observable nextCursor = null;
  @observable hasMore = true;
  @observable searchQuery = '';
  @observable sortConfig = { field: 'lastUpdated', order: 'descend' };

  DEFAULT_LIMIT = 25;

  constructor(rootStore) {
    makeObservable(this);
    this.rootStore = rootStore;

    reaction(
      () => this.rootStore.stationStore.currentStation.id,
      stationId => {
        // reset users store on logout
        if (!stationId) return this.reset();
      },
    );
  }

  @action setSorting = (field, order) => {
    const sortingChanged = field !== this.sortConfig.field || order !== this.sortConfig.order;

    if (sortingChanged) {
      this.sortConfig = { field, order };
      this.sortUsers();
    }
  };

  sortUsers = () => {
    this.users = [...this.users].sort((a, b) => {
      return this.getSortResult(a, b, this.sortConfig.field, this.sortConfig.order);
    });
  };

  getSortResult = (a, b, field, order) => {
    let result = 0;

    switch (field) {
      case 'username': {
        const aName = a.fullName || `${a.firstName || ''} ${a.lastName || ''}`.trim();
        const bName = b.fullName || `${b.firstName || ''} ${b.lastName || ''}`.trim();
        result = this.textSorter(aName, bName);
        break;
      }

      case 'email': {
        result = this.textSorter(a.email || '', b.email || '');
        break;
      }

      case 'role': {
        const aRole = a.stations?.[0]?.role?.name || a.relations?.stations?.[0]?.role?.name || '';
        const bRole = b.stations?.[0]?.role?.name || b.relations?.stations?.[0]?.role?.name || '';
        result = this.textSorter(aRole, bRole);
        break;
      }

      case 'lastUpdated':
      default: {
        const aDate = new Date(a.lastUpdated).getTime();
        const bDate = new Date(b.lastUpdated).getTime();
        if (aDate < bDate) result = -1;
        if (aDate > bDate) result = 1;
        break;
      }
    }

    return order === 'ascend' ? result : -result;
  };

  textSorter = (a, b) => {
    const aLowered = lowerCase(a || '');
    const bLowered = lowerCase(b || '');

    if (aLowered < bLowered) return -1;
    if (aLowered > bLowered) return 1;
    return 0;
  };

  @action get = async (options: GetOptions = {}) => {
    const { loadMore = false, search = this.searchQuery, resetSearch = false } = options;

    if (loadMore && !this.hasMore) return;
    try {
      if (!loadMore) {
        this.isLoading = true;
        this.users = [];
        this.nextCursor = null;
        this.hasMore = true;
      } else {
        this.isLoadingMore = true;
      }

      if (resetSearch) {
        this.searchQuery = '';
      } else if (search !== this.searchQuery) {
        this.searchQuery = search;
      }

      const params = {
        cursor: loadMore ? this.nextCursor : undefined,
        limit: this.DEFAULT_LIMIT,
        search: this.searchQuery || undefined,
      };

      Object.keys(params).forEach(key => {
        if (params[key] === undefined || params[key] === '') {
          delete params[key];
        }
      });

      const { data } = await epaxios.get('/msp/user', { params });

      const processedUsers = data.users.map(userData => new User(userData, this.rootStore));

      runInAction(() => {
        if (loadMore) {
          this.users = [...this.users, ...processedUsers];
        } else {
          this.users = processedUsers;
        }

        this.nextCursor = data.nextCursor;
        this.hasMore = !isNil(data.nextCursor);

        // Apply current sorting
        // this.sortUsers(); // Disabled for now. ANTD Table sorting will handle this
      });

      return processedUsers;
    } catch (e) {
      Sentry.captureException(new Error(`Error when getting users: ${e}`));
      throw e;
    } finally {
      runInAction(() => {
        this.isLoading = false;
        this.isLoadingMore = false;
      });
    }
  };

  @action loadMore = async () => {
    if (!this.hasMore || this.isLoadingMore) return;
    return this.get({ loadMore: true });
  };

  @action search = async query => {
    return this.get({ search: query, loadMore: false });
  };

  @action clearSearch = async () => {
    return this.get({ resetSearch: true, loadMore: false });
  };

  @action refresh = async () => {
    return this.get({ loadMore: false });
  };

  @action getById = async id => {
    this.isLoading = true;
    try {
      const { data } = await epaxios.get(`/msp/user/${id}`);
      const newUser = new User(data, this.rootStore);

      runInAction(() => {
        const existingIndex = this.users.findIndex(user => user.id === newUser.id);
        if (existingIndex >= 0) {
          this.users[existingIndex] = newUser;
        } else {
          this.users.unshift(newUser);
        }
        this.sortUsers();
      });
      return newUser;
    } catch (e) {
      Sentry.captureException(new Error(`Error when getting user: ${e}`));
      throw e;
    } finally {
      this.isLoading = false;
    }
  };

  @action create = async payload => {
    try {
      const { data } = await epaxios.post(`/msp/user`, payload);
      const newUser = new User(data, this.rootStore);

      runInAction(() => {
        this.users.push(newUser);
        this.sortUsers();
      });

      message.success(i18n.t('User created successfully'));
      return newUser;
    } catch (e) {
      if (e.response?.status === 409) {
        message.error(i18n.t('User creation failed, please contact support for further information'), 5);
      } else {
        message.error(i18n.t('User creation failed. ' + (e.response?.data?.message || '')));
      }
      throw e;
    }
  };

  @action update = async (id, payload, messageSuccess, messageError) => {
    this.isUpdating = true;
    try {
      const isUpdatedUserExternal = this.findById(id)?.isExternalUser;
      const allowedUpdatePayload = isUpdatedUserExternal ? pick(payload, ssoUserEditableFields) : payload;
      const { data } = await epaxios.patch(`/msp/user/${id}`, allowedUpdatePayload);
      const newUser = new User(data, this.rootStore);
      this.rootStore.userStore.updateStations(newUser.stations);

      runInAction(() => {
        this.users = map(this.users, user => (user.id === newUser.id ? newUser : user));
      });
      if (messageSuccess !== null) {
        message.success(i18n.t(messageSuccess ?? 'User updated successfully'));
      }
    } catch (e) {
      if (messageError !== null) {
        message.error(i18n.t(messageError ?? 'User update failed'));
      }
      return Promise.reject(e);
    } finally {
      this.isUpdating = false;
    }
  };

  @action delete = async id => {
    try {
      const payload = { userStations: [] };
      await epaxios.patch(`/msp/user/${id}`, payload);

      runInAction(() => {
        remove(this.users, { id });
      });
      message.success(i18n.t('User deleted successfully'));
    } catch (e) {
      message.error(i18n.t('User deletion failed'));
      return Promise.reject(e);
    }
  };

  @action reset = () => {
    this.users = [];
    this.isLoading = false;
    this.isLoadingMore = false;
    this.nextCursor = null;
    this.hasMore = true;
    this.searchQuery = '';
    this.sortConfig = { field: 'lastUpdated', order: 'descend' };
  };

  @action findById = id => {
    return find(this.users, { id });
  };

  @action fetchStationValueDashboard = async () => {
    try {
      const { data } = await epaxios.get(`/subscription/station-subscription-value-dashboard`);
      return data;
    } catch (e) {
      Sentry.captureException(new Error(`Error when fetching value dashboard data: ${e}`));
      return [];
    }
  };

  @computed get isEmpty() {
    return this.users.length === 0;
  }

  @computed get isSearching() {
    return this.searchQuery.trim() !== '';
  }
}

export class User {
  rootStore: typeof RootStore;
  @observable id;
  @observable firstName;
  @observable lastName;
  @observable email;
  @observable measurementSystem;
  @observable notificationsEnabled;
  @observable phoneNumber;
  @observable lastUpdated;
  @observable isExternalUser;
  @observable stations = [];
  @observable notificationSubscriptions = [];
  @observable language: LanguageEnum;

  constructor(user, rootStore) {
    makeObservable(this);
    this.rootStore = rootStore;
    this.id = user.id;
    this.firstName = user.firstName;
    this.lastName = user.lastName;
    this.email = user.email;
    this.measurementSystem = user.measurementSystem;
    this.phoneNumber = new PhoneNumber(user.phoneNumber);
    this.lastUpdated = user.lastUpdated;
    this.isExternalUser = user.isExternalUser;
    this.stations = map(user.relations?.stations || [], station => new Station(station));
    this.notificationsEnabled = user.notificationsEnabled;
    this.notificationSubscriptions = map(
      user.notificationSubscriptions || [],
      notificationSubscription => new NotificationSubscriptions(notificationSubscription),
    );
    this.language = user.language;
  }

  @computed get fullName() {
    return `${this.firstName} ${this.lastName}`.trim();
  }

  @computed get formData() {
    const stationAndSubscriptions = map(this.stations, station => {
      const foundNotificationSubscriptions = find(this.notificationSubscriptions, { stationId: station.id });
      return {
        stationId: station.id,
        roleId: station.role.id,
        isFavorite: station.isFavorite,
        subscriptions: foundNotificationSubscriptions?.formData,
      };
    });

    return {
      firstName: this.firstName,
      lastName: this.lastName,
      email: this.email,
      measurementSystem: this.measurementSystem,
      cc: this.phoneNumber.countryCode,
      number: this.phoneNumber.number,
      stationAndSubscriptions: isEmpty(stationAndSubscriptions) ? [{}] : stationAndSubscriptions,
      notificationsEnabled: this.notificationsEnabled,
      language: this.language,
    };
  }
}

class PhoneNumber {
  @observable id;
  @observable countryCode;
  @observable dialCode;
  @observable number;

  constructor(phoneNumber) {
    makeObservable(this);
    this.id = uuidv4();
    this.countryCode = phoneNumber?.countryCode;
    this.dialCode = phoneNumber?.dialCode;
    this.number = phoneNumber?.number;
  }

  @computed get countryName() {
    const found = find(dialCodes, { isoCode2digits: this.countryCode });
    return get(found, 'name', '');
  }

  @computed get fullNumber() {
    const _dialCode = this.dialCode ? `+${this.dialCode}` : undefined;
    const compactedNumber = compact([_dialCode, this.number]);
    const number = compactedNumber.join('-');

    return number;
  }
}

class Station {
  @observable id;
  @observable name;
  @observable siteName;
  @observable customerName;
  @observable role;
  @observable isFavorite;

  constructor(station) {
    makeObservable(this);
    this.id = station.id;
    this.name = station.name;
    this.siteName = station.siteName;
    this.customerName = station.customerName;
    this.role = new Role(station.role);
    this.isFavorite = station.isFavorite;
  }

  @computed get fullName() {
    return getFullStationName(this);
  }
}

class Role {
  @observable id;
  @observable name;

  constructor(role) {
    makeObservable(this);
    this.id = role.id;
    this.name = role.name;
  }
}

class NotificationSubscriptions {
  @observable id;
  @observable stationId;
  @observable presets = [];

  constructor(notificationSubscription) {
    makeObservable(this);
    this.id = uuidv4();
    this.stationId = notificationSubscription.stationId;
    this.presets = map(notificationSubscription.presets || [], preset => new Preset(preset));
  }

  @computed get formData() {
    return this.presets.reduce((acc, preset) => {
      return { ...acc, ...preset.formData };
    }, {});
  }
}

class Preset {
  @observable id;
  @observable notificationType;
  @observable preferredChannels;

  constructor(preset) {
    makeObservable(this);
    this.id = uuidv4();
    this.notificationType = preset.notificationType;
    this.preferredChannels = new PreferredChannels(preset.preferredChannels);
  }

  @computed get formData() {
    return {
      [this.notificationType]: {
        email: this.preferredChannels.email,
        sms: this.preferredChannels.sms,
      },
    };
  }
}

class PreferredChannels {
  @observable email;
  @observable sms;

  constructor(preferredChannels = []) {
    makeObservable(this);
    this.email = preferredChannels.includes('email');
    this.sms = preferredChannels.includes('sms');
  }
}
